import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeader } from "@/components/common/Widgets";
import { Coins, Plus, Trash2, Loader2, RefreshCw, CheckCircle2, ShieldCheck } from "lucide-react";
import { fmtNum } from "@/lib/format";

const ORNAMENT_TYPES = ["Necklace", "Bangles", "Chain", "Earrings", "Ring", "Bracelet", "Coin", "Anklet", "Pendant", "Other"];

export default function GoldCollateralSection({ app, reload }) {
  const { user } = useAuth();
  const [rate, setRate] = useState(null);
  const [existing, setExisting] = useState(null);
  const [items, setItems] = useState([{ ornament_type: "Necklace", description: "", pieces: 1, gross_weight_g: "", stones_weight_g: 0, purity_karat: 22 }]);
  const [appraiser, setAppraiser] = useState({ appraiser_name: user?.name || "", appraiser_license_no: "" });
  const [ltvCap, setLtvCap] = useState(75);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [r, g] = await Promise.all([
        api.get("/collateral/gold-rate"),
        api.get(`/collateral/gold/${app.id}`),
      ]);
      setRate(r.data);
      if (g.data) {
        setExisting(g.data);
        setItems(g.data.items.map(it => ({ ...it, gross_weight_g: it.gross_weight_g, stones_weight_g: it.stones_weight_g || 0 })));
        setAppraiser({ appraiser_name: g.data.appraiser_name, appraiser_license_no: g.data.appraiser_license_no || "" });
        setLtvCap(g.data.ltv_cap_pct);
      }
    } catch (e) { toast.error("Failed to load gold collateral"); }
    finally { setLoading(false); }
  }, [app.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const addItem = () => setItems(prev => [...prev, { ornament_type: "Bangles", description: "", pieces: 1, gross_weight_g: "", stones_weight_g: 0, purity_karat: 22 }]);
  const removeItem = (i) => setItems(prev => prev.filter((_, idx) => idx !== i));
  const updateItem = (i, k, v) => setItems(prev => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  // Live preview computation (mirrors backend logic)
  const previewTotal = items.reduce((sum, it) => {
    const gross = Number(it.gross_weight_g) || 0;
    const stones = Number(it.stones_weight_g) || 0;
    const net = Math.max(0, gross - stones);
    const karat = Number(it.purity_karat) || 22;
    const ratePer = (rate?.rate_24k_per_g || 0) * karat / 24;
    return sum + (net * ratePer);
  }, 0);
  const totalNetWeight = items.reduce((s, it) => s + Math.max(0, (Number(it.gross_weight_g) || 0) - (Number(it.stones_weight_g) || 0)), 0);
  const maxEligible = previewTotal * ltvCap / 100;

  const save = async () => {
    if (!appraiser.appraiser_name) return toast.error("Appraiser name is required");
    const bad = items.find(it => !it.gross_weight_g || Number(it.gross_weight_g) <= 0);
    if (bad) return toast.error("All items must have a gross weight > 0");
    setSaving(true);
    try {
      const payload = {
        items: items.map(it => ({
          ornament_type: it.ornament_type,
          description: it.description || "",
          pieces: Number(it.pieces) || 1,
          gross_weight_g: Number(it.gross_weight_g),
          stones_weight_g: Number(it.stones_weight_g) || 0,
          purity_karat: Number(it.purity_karat),
          appraiser_notes: it.appraiser_notes || "",
        })),
        appraiser_name: appraiser.appraiser_name,
        appraiser_license_no: appraiser.appraiser_license_no,
        ltv_cap_pct: Number(ltvCap),
        photographs: [],
      };
      await api.post(`/collateral/gold/${app.id}`, payload);
      toast.success("Gold collateral saved");
      loadAll();
      reload && reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      await api.post(`/collateral/gold/${app.id}/approve`, { remarks: "Appraisal approved" });
      toast.success("Gold collateral approved");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approval failed"); }
    finally { setApproving(false); }
  };

  if (loading) return <Card className="p-4 space-y-2">{[0,1,2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>;

  return (
    <div className="space-y-4" data-testid="gold-section">
      <SectionHeader
        title="Gold Collateral"
        subtitle="Capture each ornament's weight + purity. Valuation auto-computes against live 24K rate."
        action={(
          <div className="flex gap-2 items-center">
            <Button variant="ghost" size="sm" onClick={loadAll} data-testid="gold-refresh-rate"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh Rate</Button>
            <Button onClick={save} disabled={saving} data-testid="gold-save-button">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Save Collateral</Button>
          </div>
        )}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <RateBlock label="24K" rate={rate?.rate_24k_per_g} />
        <RateBlock label="22K" rate={rate?.rate_22k_per_g} highlight />
        <RateBlock label="18K" rate={rate?.rate_18k_per_g} />
      </div>
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold heading flex items-center gap-2"><Coins className="h-4 w-4 text-warning" />Items</h3>
          <Button variant="outline" size="sm" onClick={addItem} data-testid="gold-add-item"><Plus className="h-3.5 w-3.5 mr-1" />Add Item</Button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`gold-item-${i}`}>
              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Ornament</Label><Select value={it.ornament_type} onValueChange={v => updateItem(i, "ornament_type", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{ORNAMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Description</Label><Input value={it.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="e.g., Yellow gold w/ stone" /></div>
              <div className="col-span-1"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Pieces</Label><Input type="number" value={it.pieces} onChange={e => updateItem(i, "pieces", e.target.value)} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Gross (g)</Label><Input type="number" step="0.01" value={it.gross_weight_g} onChange={e => updateItem(i, "gross_weight_g", e.target.value)} data-testid={`gold-item-${i}-gross`} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Stones (g)</Label><Input type="number" step="0.01" value={it.stones_weight_g} onChange={e => updateItem(i, "stones_weight_g", e.target.value)} /></div>
              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Purity (karat)</Label><Select value={String(it.purity_karat)} onValueChange={v => updateItem(i, "purity_karat", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{[24, 22, 20, 18, 14].map(k => <SelectItem key={k} value={String(k)}>{k}K</SelectItem>)}</SelectContent></Select></div>
              <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => removeItem(i)} disabled={items.length === 1}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button></div>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Appraiser Name *</Label><Input value={appraiser.appraiser_name} onChange={e => setAppraiser({ ...appraiser, appraiser_name: e.target.value })} data-testid="gold-appraiser-name" /></div>
        <div className="space-y-1.5"><Label>Appraiser License No.</Label><Input value={appraiser.appraiser_license_no} onChange={e => setAppraiser({ ...appraiser, appraiser_license_no: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>LTV Cap (%)</Label><Input type="number" value={ltvCap} onChange={e => setLtvCap(Number(e.target.value))} /></div>
      </Card>
      <Card className="p-4 border-2 border-accent/30 bg-accent/5">
        <h3 className="text-sm font-semibold heading mb-3">Valuation Summary (live preview)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total Net Weight" value={`${fmtNum(totalNetWeight, 2)} g`} />
          <Stat label="Total Value" value={`₹ ${fmtNum(previewTotal, 0)}`} highlight />
          <Stat label="LTV Cap" value={`${ltvCap}%`} />
          <Stat label="Max Eligible Loan" value={`₹ ${fmtNum(maxEligible, 0)}`} highlight />
        </div>
      </Card>
      {existing && (
        <Card className={`p-4 border ${existing.approval_status === "APPROVED" ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {existing.approval_status === "APPROVED" ? <CheckCircle2 className="h-5 w-5 text-success" /> : <ShieldCheck className="h-5 w-5 text-warning" />}
              <div>
                <div className="text-sm font-semibold">Appraisal Status: <Badge variant="outline" className={`ml-1 text-[10px] ${existing.approval_status === "APPROVED" ? "text-success border-success/40" : "text-warning border-warning/40"}`}>{existing.approval_status}</Badge></div>
                <div className="text-xs text-muted-foreground mt-0.5">Created by {existing.created_by}{existing.approved_by ? ` · Approved by ${existing.approved_by}` : ""}</div>
              </div>
            </div>
            {existing.approval_status !== "APPROVED" && existing.created_by !== user?.name && (
              <Button size="sm" onClick={approve} disabled={approving} data-testid="gold-approve-button">{approving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}Approve as Appraiser-Checker</Button>
            )}
            {existing.created_by === user?.name && existing.approval_status !== "APPROVED" && (
              <span className="text-xs text-muted-foreground italic">Awaiting checker (cannot self-approve)</span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

function RateBlock({ label, rate, highlight }) {
  return (
    <Card className={`p-3 text-center ${highlight ? "border-warning/40 bg-warning/5" : ""}`}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label} Gold Rate</div>
      <div className={`text-xl font-bold tabular mt-1 ${highlight ? "text-warning" : ""}`}>₹ {fmtNum(rate || 0, 2)}/g</div>
    </Card>
  );
}
function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className={`text-base font-semibold tabular mt-0.5 ${highlight ? "text-accent" : ""}`}>{value}</div>
    </div>
  );
}
